import { SESSION_REFUSAL } from '@classgo/shared';
import { Button, Card, Spinner } from '@classgo/ui';
import { useNavigate, useRouter } from '@tanstack/react-router';

import { ApiError } from '../../api/client';
import { useAuthStore } from '../../auth/auth-provider';
import { useKitT } from '../../i18n';
import { useWaking } from '../../shell/waking-state';

/**
 * Shown while the session loads on a cold start.
 */
export const PendingScreen = () => {
  const t = useKitT();
  const waking = useWaking();
  return (
    <main className="flex min-h-screen items-center justify-center bg-gray-50">
      <div role="status" className="flex items-center gap-3 text-sm text-gray-500">
        <Spinner />
        {waking ? t('waking.title') : t('common.loading')}
      </div>
    </main>
  );
};

/**
 * `GET /me` failed: a 404 means the account exists in Cognito but belongs to no school; a
 * suspended school or a disabled person get their own explanation; anything else can be retried.
 * Every case offers a way out of the session, and a suspended school can retry once reactivated.
 */
export const SessionErrorScreen = ({ error }: { error: unknown }) => {
  const t = useKitT();
  const auth = useAuthStore();
  const router = useRouter();
  const navigate = useNavigate();
  const notProvisioned = error instanceof ApiError && error.code === 'NOT_FOUND';
  const refusal =
    error instanceof ApiError && error.code === 'FORBIDDEN'
      ? error.message === SESSION_REFUSAL.tenantSuspended
        ? 'suspended'
        : error.message === SESSION_REFUSAL.userDisabled
          ? 'disabled'
          : null
      : null;
  const title = refusal
    ? t(`session.${refusal}.title`)
    : notProvisioned
      ? t('session.notProvisioned.title')
      : t('session.error.title');
  const body = refusal
    ? t(`session.${refusal}.body`)
    : notProvisioned
      ? t('session.notProvisioned.body')
      : error instanceof ApiError
        ? t(`api.error.${error.code}`)
        : t('api.error.UNKNOWN');
  const canRetry = !notProvisioned && refusal !== 'disabled';
  const leave = () => {
    auth.signOutLocal('user');
    void navigate({ to: '/login', search: { redirect: undefined } });
  };
  return (
    <main className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
        <p className="mt-2 text-sm text-gray-600">{body}</p>
        <div className="mt-5 flex gap-2">
          {canRetry ? (
            <Button onClick={() => void router.invalidate()}>{t('session.retry')}</Button>
          ) : null}
          <Button variant="secondary" onClick={leave}>
            {t('shell.logout')}
          </Button>
        </div>
      </Card>
    </main>
  );
};
